import Image from "next/image";
import type { ReactNode } from "react";
import LinkButton from "@/components/ui/LinkButton";

interface ImageCardProps {
  children?: ReactNode;
  imageSrc: string;           // Pflicht: sonst macht next/image Probleme
  imageAlt?: string;
  title?: string;
  description?: string;
  linkText?: string;
  linkUrl?: string;
  className?: string;
}

export default function ImageCard({
  children,
  imageSrc,
  imageAlt = "Image",
  title,
  description,
  linkText,
  linkUrl,
  className = "",
}: ImageCardProps) {
  const hasCta = Boolean(linkText && linkUrl);

  return (
    <div
      className={[
        "group flex h-full flex-col justify-between overflow-hidden rounded-xl",
        "bg-white shadow-lg transition-all duration-300",
        "hover:-translate-y-1 hover:shadow-xl",
        className,
      ].join(" ")}
    >
      {/* Image */}
      <div className="relative aspect-16/10 w-full overflow-hidden">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          priority={false}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-6 text-center">
        {title ? (
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        ) : null}

        {description ? (
          <p className="text-slate-600">{description}</p>
        ) : null}

        {children}
      </div>

      {/* CTA */}
      {hasCta ? (
        <div className="flex justify-center px-6 pb-6">
          <LinkButton href={linkUrl!} variant="primary">
            {linkText!}
          </LinkButton>
        </div>
      ) : null}
    </div>
  );
}
